import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFetch from '../../../hooks/useFetch';
import usePost from '../../../hooks/usePost';
import usePut from '../../../hooks/usePut';
import { MwButton, MwToast } from '../../ui';
import { useCurrent } from '../../../hooks';

const AddEditCurrencyModal = ({ isOpen, onClose, mode, itemId, onSuccess }) => {
  const { t } = useTranslation('global');
  const { currentTenantId, currentCompanyId } = useCurrent();
  
  const [formData, setFormData] = useState({
    currency_code: '',
    currency_rate: '',
    name_ar: '',
    name_en: '',
    isActive: true,
    tenantId: currentTenantId,
    companyId: currentCompanyId
  });
  
  const [isLoading, setIsLoading] = useState(mode === 'edit');
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  
  const { postData } = usePost();
  const { putData } = usePut();
  
  const shouldFetchDetails = mode === 'edit' && itemId;
  const detailUrl = shouldFetchDetails ? `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/currencies/${itemId}` : null;
  
  const { data: currencyDetails, loading: fetchLoading } = useFetch(detailUrl, shouldFetchDetails);
  
  useEffect(() => {
    setIsLoading(fetchLoading);
  }, [fetchLoading]);
  
  useEffect(() => {
    if (mode === 'edit' && currencyDetails?.data) {
      const currencyData = currencyDetails.data;
      
      setFormData({
        currency_code: currencyData.currency_code?.trim() || '',
        currency_rate: currencyData.currency_rate?.toString() || '',
        name_ar: currencyData.name_ar?.trim() || '',
        name_en: currencyData.name_en?.trim() || '',
        isActive: currencyData.is_active === true || currencyData.is_active === 'true',
        tenantId: currencyData.tenant_id || currentTenantId,
        companyId: currencyData.company_id || currentCompanyId
      });
      setIsLoading(false);
    } else if (mode === 'add') {
      setFormData({
        currency_code: '',
        currency_rate: '',
        name_ar: '',
        name_en: '',
        isActive: true,
        tenantId: currentTenantId,
        companyId: currentCompanyId
      });
      setIsLoading(false);
    }
  }, [currencyDetails, mode, currentTenantId, currentCompanyId]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    await handleConfirm();
  };
  
  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      const dataToSubmit = {
        ...formData,
        currency_rate: formData.currency_rate.toString(),
        is_active: formData.isActive
      };
      
      if (mode === 'add') {
        await postData(`${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/currencies`, dataToSubmit);
        setToastMessage(t('currency.addSuccess', 'تم إضافة العملة بنجاح'));
        setShowToast(true);
      } else {
        await putData(`${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/currencies/update/${itemId}`, dataToSubmit);
        setToastMessage(t('currency.updateSuccess', 'تم تحديث العملة بنجاح'));
        setShowToast(true);
      }
      
      setIsLoading(false);
      onSuccess();
      onClose();
    } catch (error) {
      setToastMessage(t('common.errorSaving', 'حدث خطأ أثناء الحفظ. يرجى المحاولة مرة أخرى.'));
      setShowToast(true);
      setIsLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-gray-800 bg-opacity-50 flex justify-center items-center text-xs">
      <MwToast msg={toastMessage} isShow={showToast} setIsShow={setShowToast} />
      <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6 relative">
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-80 flex flex-col justify-center items-center rounded-lg z-10">
            <div className="w-12 h-12 rounded-full border-4 border-blue-100 border-t-blue-500 animate-spin"></div>
            <p className="mt-4 text-blue-600 font-medium">
              {mode === 'edit' ? t('common.loading', 'جاري التحميل...') : t('common.saving', 'جاري الحفظ...')}
            </p>
          </div>
        )}
        
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-gray-500 text-base">
            {mode === 'add' ? t('currency.addNew', 'إضافة عملة جديدة') : t('currency.edit', 'تعديل العملة')}
          </h2>
          <button
            onClick={onClose}
            type="button"
            className="text-gray-500 hover:text-gray-700"
            disabled={isLoading}
          >
            ✕
          </button>
        </div>
        
        <form onSubmit={handleFormSubmit}>
          <div className="mb-4">
            <label className="block text-gray-500 text-sm mb-2">
              {t('currency.nameAr', 'الاسم بالعربية')}
            </label>
            <input
              type="text"
              name="name_ar"
              value={formData.name_ar}
              onChange={handleChange}
              className="w-full p-2 border rounded bg-white"
              required
              disabled={isLoading}
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-500 text-sm mb-2">
              {t('currency.nameEn', 'الاسم بالإنجليزية')}
            </label>
            <input
              type="text"
              name="name_en"
              value={formData.name_en}
              onChange={handleChange}
              className="w-full p-2 border rounded bg-white"
              required
              disabled={isLoading}
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-500 text-sm mb-2">
              {t('currency.code', 'رمز العملة')}
            </label>
            <input
              type="text"
              name="currency_code"
              value={formData.currency_code}
              onChange={handleChange}
              className="w-full p-2 border rounded bg-white"
              required
              disabled={isLoading}
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-500 text-sm mb-2">
              {t('currency.rate', 'سعر الصرف')}
            </label>
            <input
              type="number"
              name="currency_rate"
              value={formData.currency_rate}
              onChange={handleChange}
              className="w-full p-2 border rounded bg-white"
              min="0"
              step="0.01"
              required
              disabled={isLoading}
            />
          </div>
          
          <div className="mb-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="mr-2"
                disabled={isLoading}
              />
              <span className="text-gray-500 text-sm">
                {t('currency.active', 'نشط')}
              </span>
            </label>
          </div>
          
          <div className="flex justify-end gap-2">
            <MwButton
              type="cancelBtn"
              size='sm'
              onClick={onClose}
              className="mr-2 px-4 py-2 text-gray-500 hover:text-gray-700"
              disabled={isLoading}
            >
              {t('common.cancel', 'إلغاء')}
            </MwButton>
            <MwButton
              type="bottomBtn"
              size='sm'
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              disabled={isLoading}
            >
              {mode === 'add' ? t('common.add', 'إضافة') : t('common.save', 'حفظ')}
            </MwButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditCurrencyModal;
